import React, { useMemo, useCallback, useState, useEffect } from 'react';

import { useNavigate, useLocation, useParams } from 'react-router-dom';

import { FORM_ERROR } from 'final-form';

import InviteForm from './InviteForm';

import Layout from '../components/common/Layout';
import { composeValidators, required, max, min, password } from '../helpers/validation';
import {
  acceptInvitation,
  getCredentialsFromInvitation,
  phoneVerificationRegister,
  phoneVerificationResetResendCode,
} from '../service/auth';

export default function Invite() {
  const location = useLocation();
  const [credentials, setCredentials] = useState({});
  const navigate = useNavigate();
  const pathnames = useParams();
  const inviteToken = pathnames.code;
  const [phone, setPhone] = useState();
  const [intentId, setIntentId] = useState();
  const query = useMemo(() => new URLSearchParams(location.search), [location]);
  const destination = query.get('app');
  const [userAccountConsentAccepted, setUserAccountConsentAccepted] = useState(false);

  const formOptions = useMemo(() => {
    return {
      formName: 'any',
      fields: [
        {
          label: 'First name',
          placeholder: 'Enter your first name',
          required: true,
          validate: composeValidators(
            required("First name can't be empty"),
            max('Maximum length is 50 characters', 50)
          ),
          name: 'firstname',
        },
        {
          label: 'Last name',
          placeholder: 'Enter your last name',
          name: 'lastname',
          validate: composeValidators(
            required("Last name can't be empty"),
            max('Maximum length is 50 characters', 50)
          ),
          required: true,
        },
        {
          label: 'Phone Number',
          required: true,
          type: 'tel',
          name: 'phone',
          validate: composeValidators(required("Phone can't be empty")),
          disabled: true,
          isLabel: true,
        },
        {
          label: 'Password',
          placeholder: 'Enter your password',
          required: true,
          type: 'password',
          validate: composeValidators(
            required("Password can't be empty"),
            min('Password must be at least 8 characters long', 8),
            max('Maximum length is 64 characters', 64),
            password('Must have 8 characters, uppercase & lowercase letters, one number')
          ),
          name: 'password',
        },
        {
          label: 'Confirm Password',
          placeholder: 'Enter your password',
          required: true,
          type: 'password',
          validate: composeValidators(
            required('You must confirm password'),
            max('Maximum length is 64 characters', 64)
          ),
          name: 'confirmpassword',
        },
      ],
    };
  }, []);

  const handleTermChecks = () => {
    setUserAccountConsentAccepted(!userAccountConsentAccepted);
  };

  const getCredentials = async (inviteToken) => {
    try {
      const userCredentials = await getCredentialsFromInvitation(inviteToken);
      setCredentials(userCredentials.data);
    } catch (error) {
      const errorCode = error.response.data.errors[0].code;
      if (
        error.response.status === 401 &&
        (errorCode === 'USER_NOT_PRESENT_OR_BAD_CREDENTIALS' ||
          errorCode === 'INVITATION_ALREADY_ACCEPTED')
      ) {
        navigate({
          pathname: '/error/invite',
          state: {
            title: 'There is no such invite for a user',
            message:
              'This invite was already accepted, or not created. If you already accepted one, try to sign in.',
            linkButtonText: 'signin',
            linkTo: `/auth/sign-in?app=${destination || '/dashboard'}`,
          },
        });
      }
    }
  };

  const onSubmit = useCallback(
    async (values) => {
      return await acceptInvitation({
        ...values,
        inviteToken,
        phone: values.phone.replaceAll('-', ''),
      });
    },
    [inviteToken]
  );

  const handleFormSubmit = useCallback(
    async (values) => {
      if (destination !== '/admin' && !userAccountConsentAccepted) {
        const message = 'Please review and accept the above consent to proceed.';
        // NotificationManager.warning(message, 'Warning');
        return { [FORM_ERROR]: message };
      } else {
        try {
          const userData = await onSubmit(values);
          if (userData.data.profile && userData.data.profile.accessToken) {
            navigate({
              pathname: '/auth/success',
              search: `?token=${userData.data.profile.accessToken}&refresh=${userData.data.profile.refreshToken}`,
            });
          } else {
            //we need to do MFA
            const { phone, intentId } = userData.data?.intentResponse;
            setPhone(phone);
            setIntentId(intentId);
          }
        } catch (err) {
          return { [FORM_ERROR]: 'Please check your credentials' };
        }
      }
    },
    [navigate, onSubmit, userAccountConsentAccepted]
  );

  useEffect(() => {
    getCredentials(inviteToken);
  }, []);

  const parsePhoneNumber = (phoneNumber) => {
    return `${phoneNumber.slice(0, 2)}-${phoneNumber.slice(2, 5)}-${phoneNumber.slice(
      5,
      8
    )}-${phoneNumber.slice(8)}`;
  };

  const onPhoneVerificationSubmit = useCallback(
    async ({ code }) => {
      let response = null;
      response = await phoneVerificationRegister({ code, intentId });
      const { token, refreshToken } = response.data;

      if (destination === '/admin') {
        return navigate({
          pathname: '/auth/success',
          search: `?token=${token}&refresh=${refreshToken}&app=/admin`,
        });
      }
      navigate(`/token?token=${token}&refresh=${refreshToken}&app=${destination || '/dashboard'}`);
    },
    [destination, navigate, intentId]
  );

  const onPhoneVerificationRecoverySubmit = useCallback(async () => {
    await phoneVerificationResetResendCode({ intent: intentId });
  }, [intentId]);

  if (destination === '/admin') {
    return (
      <InviteForm
        phone={phone}
        intentId={intentId}
        destination={destination || '/admin'}
        formOptions={formOptions}
        credentials={credentials}
        handleTermChecks={handleTermChecks}
        parsePhoneNumber={parsePhoneNumber}
        handleFormSubmit={handleFormSubmit}
        onPhoneVerificationSubmit={onPhoneVerificationSubmit}
        onPhoneVerificationRecoverySubmit={onPhoneVerificationRecoverySubmit}
      />
    );
  }
  return (
    <Layout>
      <div className="auth section">
        <InviteForm
          phone={phone}
          intentId={intentId}
          destination={destination}
          formOptions={formOptions}
          credentials={credentials}
          parsePhoneNumber={parsePhoneNumber}
          handleFormSubmit={handleFormSubmit}
          handleTermChecks={handleTermChecks}
          onPhoneVerificationSubmit={onPhoneVerificationSubmit}
          onPhoneVerificationRecoverySubmit={onPhoneVerificationRecoverySubmit}
        />
      </div>
    </Layout>
  );
}
